import { useCallback, useMemo, useState } from 'react';

import { useToast } from '@vimeo/bokeh';
import { CircleExclamationFilled } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { Viewer } from '@vimeo/viewer';

import { uriToShowcaseLink } from '../components/NewContentMenu/utils';
import { trackNavigationActionEvent } from '../tracking';
import { useCanCreateShowcase } from './use-can-create-showcase';
import { useCreateShowcase } from './use-create-showcase';
import { useShowcases } from './use-showcases';

const FREE_USER_SHOWCASE_LIMIT = 3;

interface Props {
  canManageTeamCollections?: boolean;
  hasOwnerPaid?: boolean;
  viewer: Viewer;
}

/**
 * Hook to manage the state and actions for creating a new showcase.
 */
export const useShowcaseNewDropdownMenu = ({
  canManageTeamCollections,
  hasOwnerPaid,
  viewer,
}: Props) => {
  const toast = useToast();
  const [isShowcaseUpsellModalActive, setIsShowcaseUpsellModalActive] = useState(false);
  const ownerId = viewer?.teamUser?.ownerId || viewer?.user?.id;
  const canCreateShowcase = useCanCreateShowcase({ canManageTeamCollections });
  const { total } = useShowcases({ canCreateShowcase, ownerId });

  const hasReachedShowcaseLimit = !hasOwnerPaid && Number(total) >= FREE_USER_SHOWCASE_LIMIT;

  const handleCreateShowcaseSuccess = useCallback(({ uri }: { uri: string }) => {
    window.location.href = uriToShowcaseLink(uri);
  }, []);

  const handleCreateShowcaseFailure = useCallback(() => {
    toast({
      title: translate('Sorry, something weird happened. Please try again.'),
      variant: 'warning',
      icon: <CircleExclamationFilled color="status-destructive-primary" />,
      isClosable: false,
    });
    // Only show the toast once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [createShowcase, { loading: isCreatingShowcase }] = useCreateShowcase({
    onFailure: handleCreateShowcaseFailure,
    onSuccess: handleCreateShowcaseSuccess,
    teamOwnerId: ownerId,
  });

  const handleCreateShowcase = useMemo(
    () => (hasReachedShowcaseLimit ? () => setIsShowcaseUpsellModalActive(true) : createShowcase),
    [hasReachedShowcaseLimit, setIsShowcaseUpsellModalActive, createShowcase],
  );

  const onCreateShowcaseClick = useCallback(() => {
    trackNavigationActionEvent({
      additionalFields: {
        collection_type: 'showcase',
      },
      copy: 'create_showcase',
      element: 'dropdown',
      entityType: 'showcase',
      eventName: 'vimeo.create_collection',
      feature: 'showcases',
      product: 'collections',
      target: 'showcase_manage',
      version: 4,
      viewer,
    });
    handleCreateShowcase();
  }, [handleCreateShowcase, viewer]);

  return {
    canCreateShowcase,
    isCreatingShowcase,
    isShowcaseUpsellModalActive,
    onCreateShowcaseClick,
    setIsShowcaseUpsellModalActive,
  };
};
